'use client'

import * as React from 'react'
import { cn } from '../../helpers/index.js'
import { Text, Picture, Container, Heading } from '../../components/index.js'
import { cva } from 'class-variance-authority'
import type { GiftTierShelfProps, GiftTierItem, Media } from './types.js'
import { Swiper, SwiperSlide } from 'swiper/react'
import * as SwiperModules from 'swiper/modules'
import type { Swiper as SwiperType } from 'swiper'
import { withLayout } from '../../shared/Styles.js'
import { useAiuiContext } from '../AiuiProvider/index.js'
import type { Product, ProductVariant } from '../Listing/types/product.js'
import { formatPrice } from '../Listing/utils/index.js'

/**
 * Card style variants
 */
const cardVariants = cva(
  'lg-desktop:h-[400px] rounded-card desktop:pt-4 desktop:pb-6 relative flex h-[320px] flex-col items-start gap-1 overflow-hidden p-4',
  {
    variants: {
      theme: {
        light: 'bg-gradient-to-b from-[#FCCA7B] to-[#FFE6BF]',
        dark: 'bg-gradient-to-b from-[#3A3A3A] to-[#1D1D1F]',
      },
    },
    defaultVariants: {
      theme: 'light',
    },
  }
)

/**
 * Text color variants
 */
const textVariants = cva('', {
  variants: {
    theme: {
      light: 'text-[#080A0F]',
      dark: 'text-white',
    },
  },
  defaultVariants: {
    theme: 'light',
  },
})

/**
 * Member Tag Component
 */
const MemberTag = React.memo(({ image }: { image?: Media }) => {
  if (!image) return null

  return (
    <Picture
      source={image.url}
      alt={image.alt || 'Member Tag'}
      className="h-full w-auto"
      imgClassName="h-full w-auto object-contain"
    />
  )
})

MemberTag.displayName = 'MemberTag'

/**
 * Timeline Item Component - 单个时间轴节点
 */
const TimelineItem = React.memo(({ theme = 'light' }: { theme?: 'light' | 'dark' }) => {
  const trackColor = theme === 'light' ? 'bg-[#f6cd4e]' : 'bg-[#F5F6F7]'

  return (
    <div className="flex h-4 w-full items-center">
      <div className={cn('flex h-1 w-full items-center justify-center', trackColor)}>
        {/* Handle */}
        <div className={cn('relative size-4 shrink-0 rounded-full', trackColor)}></div>
      </div>
    </div>
  )
})

TimelineItem.displayName = 'TimelineItem'

/**
 * Gift Tier Card Component
 */
const GiftTierCard = React.memo(
  ({
    item,
    theme = 'light',
    className,
    onClick,
    buildData,
  }: {
    item: GiftTierItem
    theme?: 'light' | 'dark'
    className?: string
    onClick?: () => void
    buildData?: {
      products: Product[]
    }
  }) => {
    const { locale = 'us' } = useAiuiContext()
    // 获取第一个产品的简化数据
    const product = item.products?.[0]

    const fullProduct = React.useMemo(() => {
      if (!product?.handle || !buildData?.products?.length) return null
      return buildData.products.find(p => p.handle === product.handle) || null
    }, [product?.handle, buildData?.products])

    const fullProductVariant = React.useMemo(
      () => fullProduct?.variants?.find(variant => variant?.sku === product?.sku) || ({} as ProductVariant),
      [product?.sku, fullProduct?.variants]
    )

    const price = formatPrice({
      locale: locale || 'us',
      amount: product?.custom_price ?? fullProductVariant?.price?.amount,
      currencyCode: fullProduct?.price?.currencyCode || 'USD',
    })

    return (
      <div className={cn(cardVariants({ theme }), className)} onClick={onClick} data-ui-component-id="GiftTierCard">
        {/* Member Tag */}
        <div className="lg-desktop:h-[28px] z-10 h-6">
          {item.showMemberTag && item.memberTagImage && <MemberTag image={item.memberTagImage} />}
        </div>

        {/* Product Image */}
        <div className="relative z-10 mx-auto min-h-0 w-fit flex-1">
          <Picture
            source={fullProductVariant?.image?.url || fullProduct?.images[0]?.url}
            className="size-full"
            imgClassName="size-full object-contain"
          />
        </div>

        {/* Product Info */}
        <div className={cn('z-10 flex w-full shrink-0 flex-col gap-2', textVariants({ theme }))}>
          {/* Price */}
          <div className="flex items-end gap-1">
            <Heading html={price} size={4} className="font-bold leading-[1.2] tracking-[-0.04em]" />
            {item.valueUnit && (
              <Text
                html={item.valueUnit}
                className="lg-desktop:text-2xl text-xl font-bold leading-[1.2] tracking-[-0.04em]"
              />
            )}
          </div>

          {/* Product Name */}
          <Text
            html={fullProductVariant?.title || fullProduct?.title}
            className="lg-desktop:text-2xl line-clamp-2 min-h-[48px] text-wrap text-xl font-bold leading-[1.2] tracking-[-0.04em]"
          />
        </div>
      </div>
    )
  }
)

GiftTierCard.displayName = 'GiftTierCard'

/**
 * GiftTierShelf - 满赠货架组件
 *
 * @description 阶梯式满赠活动展示，支持多个档位的产品展示和时间轴
 */
const GiftTierShelf = React.forwardRef<HTMLDivElement, GiftTierShelfProps>(
  ({ classNames = {}, data, onCardClick, buildData, ...rest }, ref) => {
    const theme = data.theme || 'light'
    const showTimeline = data.showTimeline !== false

    // Swiper 实例状态，用于联动控制
    const [cardSwiper, setCardSwiper] = React.useState<SwiperType | null>(null)
    const [timelineSwiper, setTimelineSwiper] = React.useState<SwiperType | null>(null)

    // 过滤出可售产品，确保卡片与 Timeline 同步
    const availableItems = React.useMemo(() => {
      if (!buildData?.products?.length) return data.items

      return data.items.filter(item => {
        const product = item.products?.[0]
        if (!product?.handle) return true // 没有关联产品时默认显示

        const fullProduct = buildData.products.find(p => p.handle === product.handle)
        const variant = fullProduct?.variants?.find(v => v?.sku === product?.sku)

        return variant?.availableForSale !== false
      })
    }, [data.items, buildData?.products])

    // Card Swiper breakpoints
    const cardSwiperBreakpoints = React.useMemo(() => {
      const itemCount = availableItems.length
      return {
        0: { slidesPerView: 1.2, spaceBetween: 12 },
        768: { slidesPerView: Math.min(itemCount, 2.5), spaceBetween: 12 },
        1025: { slidesPerView: Math.min(itemCount, 3.5), spaceBetween: 16 },
        1440: { slidesPerView: Math.min(itemCount, 4), spaceBetween: 16 },
      }
    }, [availableItems.length])

    // Timeline Swiper breakpoints (spaceBetween 为 0)
    const timelineSwiperBreakpoints = React.useMemo(() => {
      const itemCount = availableItems.length
      return {
        0: { slidesPerView: 1.2, spaceBetween: 0 },
        768: { slidesPerView: Math.min(itemCount, 2.5), spaceBetween: 0 },
        1025: { slidesPerView: Math.min(itemCount, 3.5), spaceBetween: 0 },
        1440: { slidesPerView: Math.min(itemCount, 4), spaceBetween: 0 },
      }
    }, [availableItems.length])

    const swiperModules = React.useMemo(() => (SwiperModules.Controller ? [SwiperModules.Controller] : []), [])

    return (
      <Container
        ref={ref}
        className={cn(classNames?.root)}
        childClassName="overflow-hidden"
        data-ui-component-id="GiftTierShelf"
        {...rest}
      >
        {/* Cards Swiper */}
        <Swiper
          modules={swiperModules}
          breakpoints={cardSwiperBreakpoints}
          className="w-full !overflow-visible"
          onSwiper={setCardSwiper}
          controller={{ control: timelineSwiper }}
        >
          {availableItems.map((item, index) => (
            <SwiperSlide key={'giftTierItem' + index}>
              <GiftTierCard
                item={item}
                theme={theme}
                buildData={buildData}
                className={classNames?.card}
                onClick={() => onCardClick?.(item, index)}
              />
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Timeline */}
        {showTimeline && (
          <div className="mt-4 flex flex-col gap-4">
            {/* Timeline Swiper */}
            <div className={cn('w-full', classNames?.timeline)}>
              <Swiper
                modules={swiperModules}
                breakpoints={timelineSwiperBreakpoints}
                className="w-full !overflow-visible"
                onSwiper={setTimelineSwiper}
                controller={{ control: cardSwiper }}
              >
                {availableItems.map((item, index) => (
                  <SwiperSlide key={'timelineItem' + index}>
                    <div className="flex flex-col items-center">
                      <TimelineItem theme={theme} />
                      <Text
                        html={item.threshold}
                        className={cn(
                          'lg-desktop:text-2xl text-center text-xl font-bold leading-[1.2] tracking-[-0.04em]',
                          textVariants({ theme }),
                          classNames?.thresholdText
                        )}
                      />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>
        )}
      </Container>
    )
  }
)

GiftTierShelf.displayName = 'GiftTierShelf'

export default withLayout(GiftTierShelf)
export type { GiftTierShelfProps, GiftTierShelfData, GiftTierItem, GiftTierProduct } from './types.js'
